export type SameSite = "Strict" | "Lax" | "None";

export interface CookieOptions {
  maxAgeSeconds?: number;
  path?: string;
  sameSite?: SameSite;
  secure?: boolean;
}

const isBrowser = () => typeof document !== "undefined";

const isSecureContext = () =>
  typeof window !== "undefined" && window.location?.protocol === "https:";

export const getCookie = (name: string): string | null => {
  if (!isBrowser()) return null;

  const prefix = `${encodeURIComponent(name)}=`;

  const entry = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));

  if (!entry) return null;

  const rawValue = entry.slice(prefix.length);

  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
};

export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): boolean => {
  if (!isBrowser()) return false;

  const {
    maxAgeSeconds,
    path = "/",
    sameSite = "Lax",
    secure = isSecureContext(),
  } = options;

  const parts = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `path=${path}`,
    `SameSite=${sameSite}`,
  ];

  if (typeof maxAgeSeconds === "number") {
    parts.push(`max-age=${Math.floor(maxAgeSeconds)}`);
    parts.push(
      `expires=${new Date(Date.now() + maxAgeSeconds * 1000).toUTCString()}`
    );
  }

  if (secure || sameSite === "None") parts.push("Secure");

  try {
    document.cookie = parts.join("; ");
  } catch {
    return false;
  }

  return getCookie(name) === value;
};

export const removeCookie = (name: string, options: CookieOptions = {}) => {
  if (!isBrowser()) return;

  const { path = "/", sameSite = "Lax", secure = isSecureContext() } = options;

  const parts = [
    `${encodeURIComponent(name)}=`,
    `path=${path}`,
    `SameSite=${sameSite}`,
    "max-age=0",
    "expires=Thu, 01 Jan 1970 00:00:00 GMT",
  ];

  if (secure || sameSite === "None") parts.push("Secure");

  try {
    document.cookie = parts.join("; ");
  } catch {
    return;
  }
};

export const hasCookie = (name: string) => getCookie(name) !== null;
